import { createSelector } from "reselect";

import AnnouncementPageId from "@mapmycustomers/shared/enum/announcements/AnnouncementPageId";
import Announcement from "@mapmycustomers/shared/types/announcements/Announcement";

import {
  getCurrentUserId,
  getOrganizationAddOnServices,
  getOrganizationId,
  getRole,
  getUserAddOnServices,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import AnnouncementFilterContext from "@app/types/AnnouncementFilterContext";
import getWebAppVersion from "@app/util/announcements/getWebAppVersion";
import selectEligibleAnnouncements from "@app/util/announcements/selectEligibleAnnouncements";
import loggingService from "@app/util/logging";

const announcementsState = (state: RootState) => state.announcements;

const pageIdArgument = (_: RootState, pageId: AnnouncementPageId | undefined) => pageId;

export const getAnnouncementQueue = createSelector(announcementsState, ({ queue }) => queue);

export const getCurrentAnnouncementIndex = createSelector(
  announcementsState,
  ({ currentIndex }) => currentIndex
);

export const isAnnouncementsModalOpen = createSelector(announcementsState, ({ open }) => open);

export const getAnnouncementsModalPageId = createSelector(
  announcementsState,
  ({ modalPageId }) => modalPageId
);

export const getAnnouncementsModalTrigger = createSelector(
  announcementsState,
  ({ modalTrigger }) => modalTrigger
);

export const getAnnouncementsPendingTrigger = createSelector(
  announcementsState,
  ({ pendingTrigger }) => pendingTrigger
);

export const isAnnouncementsHiddenForSession = createSelector(
  announcementsState,
  ({ hiddenForSession }) => hiddenForSession
);

export const isAnnouncementsLoaded = createSelector(announcementsState, ({ loaded }) => loaded);

export const isAnnouncementsLoading = createSelector(announcementsState, ({ loading }) => loading);

export const isAnnouncementsStartupWaitOver = createSelector(
  announcementsState,
  ({ startupWaitOver }) => startupWaitOver
);

// The role dimension is called roleKey in the API contract, but the client model carries it as
// me.role.key — there is no roleKey property on User.
export const getAnnouncementFilterContext = createSelector(
  getRole,
  getOrganizationId,
  getOrganizationAddOnServices,
  getUserAddOnServices,
  getCurrentUserId,
  pageIdArgument,
  (role, orgId, orgAddOns, userAddOns, userId, pageId): AnnouncementFilterContext => ({
    appVersion: getWebAppVersion(),
    orgAddOns: orgAddOns ?? {},
    orgId,
    pageId,
    roleKey: role?.key,
    userAddOns: userAddOns ?? {},
    userId,
  })
);

// The filter context selector rebuilds on every page change, so the eligibility pass reruns then
// too. Without this, one broken announcement would log on every navigation.
const reportedBrokenIds = new Set<Announcement["id"]>();

export const clearReportedBrokenIds = () => reportedBrokenIds.clear();

export const getEligibleAnnouncements = createSelector(
  announcementsState,
  getAnnouncementFilterContext,
  ({ announcements, postponedIds, viewedIds }, filterContext): Announcement[] =>
    selectEligibleAnnouncements(
      announcements.filter(({ id }) => !postponedIds.includes(id) && !viewedIds.includes(id)),
      filterContext,
      (announcement, reason, unexpectedError) => {
        if (reportedBrokenIds.has(announcement.id)) {
          return;
        }
        reportedBrokenIds.add(announcement.id);
        if (unexpectedError !== undefined) {
          loggingService.error("Failed to evaluate announcement filter", {
            announcementId: announcement.id,
            announcementName: announcement.name,
            error: unexpectedError,
            reason,
          });
          return;
        }
        loggingService.error("Skipping broken announcement", {
          announcementId: announcement.id,
          announcementName: announcement.name,
          reason,
        });
      }
    )
);
